import axios from 'axios';
import { attachAuthInterceptor } from './authInterceptor';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * Scheduled reports: recipients, the PDF toggle, and preview / send-now for the
 * weekly business report and the daily ops alert.
 *
 * Every endpoint here is super-admin only (`require_super_admin_with_ip_check`), so a
 * plain ADMIN gets 403 — the nav entry is hidden for them rather than failing later.
 */
const reportsAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// Session-expiry handling (401 -> clear + redirect), consistent with feature services.
attachAuthInterceptor(reportsAPI);

reportsAPI.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ok = (data) => ({ success: true, data });
const fail = (error) => ({ success: false, error: extractApiError(error) });

const reportsService = {
  // ---------- Recipients ----------
  async getRecipients(reportType = 'weekly_business') {
    try {
      const res = await reportsAPI.get('/report-recipients', { params: { report_type: reportType } });
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  /**
   * `value` is normalized server-side (email lowercased, mobile reduced to its last 10
   * digits), so callers must re-render from the response rather than the typed text.
   * 409 = duplicate, 400 = invalid email/mobile — both surface via extractApiError.
   */
  async addRecipient({ channel, value, label, reportType = 'weekly_business' }) {
    try {
      // report_type is a QUERY param on this endpoint — ReportRecipientCreate only
      // accepts {channel, value, label}, so putting it in the body silently drops it
      // and every recipient lands under the default type.
      const res = await reportsAPI.post(
        '/report-recipients',
        { channel, value, ...(label ? { label } : {}) },
        { params: { report_type: reportType } },
      );
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  async setRecipientActive(id, isActive) {
    try {
      const res = await reportsAPI.patch(`/report-recipients/${id}`, { is_active: isActive });
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  async deleteRecipient(id) {
    try {
      const res = await reportsAPI.delete(`/report-recipients/${id}`);
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  // ---------- Settings ----------
  async getSettings() {
    try {
      const res = await reportsAPI.get('/report-settings');
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  /** `enabled` goes as a QUERY param, not a JSON body — a body 422s. */
  async setSetting(key, enabled) {
    try {
      const res = await reportsAPI.put(`/report-settings/${key}`, null, { params: { enabled } });
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  // ---------- Weekly business report ----------
  async previewWeekly() {
    try {
      const res = await reportsAPI.get('/reports/weekly/preview');
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  async sendWeeklyNow() {
    try {
      const res = await reportsAPI.post('/reports/weekly/send-now');
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  // ---------- Daily ops alert ----------
  async previewOpsAlert() {
    try {
      const res = await reportsAPI.get('/reports/ops-alert/preview');
      return ok(res.data);
    } catch (error) { return fail(error); }
  },

  /** Without `force` a clean day sends nothing at all — that is not an error. */
  async sendOpsAlertNow(force = false) {
    try {
      const res = await reportsAPI.post('/reports/ops-alert/send-now', null, { params: { force } });
      return ok(res.data);
    } catch (error) { return fail(error); }
  },
};

export default reportsService;
